import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import DrinkListExport from '../components/DrinkListExport';
import { ToastContainer } from '../components/Toast';
import {
  PlusIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  CalendarIcon,
  CreditCardIcon,
  UserPlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';

interface Member {
  mg_nr: number;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  foto_path?: string;
  foto_path_updated?: number;
  is_guest?: boolean;
}

interface Event {
  id: number;
  beschriftung: string;
  abrechnungsmonat?: string | null;
  title?: string;
  date?: string;
  document_id?: number | null;
  document_name?: string | null;
  document_path?: string | null;
}

interface DocumentItem {
  id: number;
  name: string;
  file_path: string;
  created_at: string;
}

interface DrinkEntry {
  id: number;
  member_id: number;
  month: number; // Event ID
  amount: number;
  paid: number;
  paid_date?: string;
  paid_by?: number;
  payment_method?: 'cash' | 'paypal' | null;
  token?: string | null;
  event_label: string;
}

interface GroupedDrinkData {
  member_id: number;
  name: string;
  vorname: string;
  email: string;
  foto_path?: string;
  foto_path_updated?: number;
  is_guest?: boolean;
  roles?: string[];
  role_descriptions?: string[];
  drinks: Array<{
    drink_id: number;
    event_id: number;
    event_label: string;
    amount: number;
    paid: number;
    paid_date?: string;
    payment_method?: string;
    token?: string | null;
  }>;
  totals: {
    total: number;
    paid: number;
    open: number;
  };
}

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onConfirm?: () => void;
}

interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

const CARD_BG = '#1c2432';

const VerzehrkostenBeta: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [drinkEntries, setDrinkEntries] = useState<DrinkEntry[]>([]);
  const [, setGroupedData] = useState<GroupedDrinkData[]>([]);
  const [useGroupedApi, setUseGroupedApi] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'open' | 'with_entries' | 'hide_paid_events'>('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventMonth, setNewEventMonth] = useState(new Date().getMonth() + 1);
  const [newEventYear, setNewEventYear] = useState(new Date().getFullYear());
  const [showQuickEntryModal, setShowQuickEntryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [quickEntryEventId, setQuickEntryEventId] = useState<number | null>(null);
  const [quickEntryIndex, setQuickEntryIndex] = useState(0);
  const [quickEntryValue, setQuickEntryValue] = useState('');
  const [quickEntryValues, setQuickEntryValues] = useState<Record<number, string>>({});
  const [editingEntry, setEditingEntry] = useState<{ memberId: number; eventId: number; amount: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  const [showGuests, setShowGuests] = useState(false); // Default: hide guests without entries
  const [docPickerEventId, setDocPickerEventId] = useState<number | null>(null);
  const [availableDocuments, setAvailableDocuments] = useState<DocumentItem[]>([]);
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [docLoading, setDocLoading] = useState(false);
  const [docMenuEventId, setDocMenuEventId] = useState<number | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'events' | 'history'>('overview');

  useEffect(() => {
    fetchData();
  }, []);

  // Body-Scroll sperren wenn ein Modal offen ist
  useEffect(() => {
    const modalOpen = docMenuEventId !== null || docPickerEventId !== null || showNewEventModal;
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [docMenuEventId, docPickerEventId, showNewEventModal]);

  // Dokument-Vorschau laden wenn Modal geöffnet wird
  useEffect(() => {
    if (docMenuEventId === null) return;
    const ev = events.find(e => e.id === docMenuEventId);
    if (!ev?.document_id) return;

    setDocPreviewLoading(true);
    setDocPreviewUrl(null);
    const fileRef = ev.document_path || ev.document_name || '';
    const isPdf = /\.pdf$/i.test(fileRef);

    apiClient.get(`/api/documents/download/${ev.document_id}`, { responseType: 'blob' })
      .then(res => {
        const mimeType = isPdf ? 'application/pdf' : res.headers['content-type'] || 'image/jpeg';
        setDocPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: mimeType })));
      })
      .catch(() => setDocPreviewUrl('error'))
      .finally(() => setDocPreviewLoading(false));

    return () => {
      // Cleanup blob URL beim Schließen
      setDocPreviewUrl(prev => {
        if (prev && prev !== 'error') URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [docMenuEventId]);

  const addToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Dokument-Picker öffnen
  const openDocPicker = async (eventId: number) => {
    setDocPickerEventId(eventId);
    setDocSearchTerm('');
    setDocLoading(true);
    try {
      const res = await apiClient.get('/api/documents');
      setAvailableDocuments(res.data || []);
    } catch (err) {
      console.error('Dokumente laden fehlgeschlagen:', err);
    } finally {
      setDocLoading(false);
    }
  };

  // Dokument an Event verknüpfen
  const linkDocumentToEvent = async (eventId: number, documentId: number) => {
    try {
      await apiClient.put(`/api/events/${eventId}/document`, { document_id: documentId });
      setDocPickerEventId(null);
      addToast('success', 'Dokument verknüpft');
      await fetchData();
    } catch (err) {
      console.error('Dokument verknüpfen fehlgeschlagen:', err);
      addToast('error', 'Fehler beim Verknüpfen');
    }
  };

  // Dokument von Event entfernen
  const unlinkDocumentFromEvent = async (eventId: number) => {
    try {
      await apiClient.put(`/api/events/${eventId}/document`, { document_id: null });
      addToast('info', 'Dokument entfernt');
      await fetchData();
    } catch (err) {
      console.error('Dokument entfernen fehlgeschlagen:', err);
      addToast('error', 'Fehler beim Entfernen');
    }
  };

  // Dokument herunterladen
  const downloadEventDocument = async (event: Event) => {
    if (!event.document_id) return;
    try {
      const response = await apiClient.get(`/api/documents/download/${event.document_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = event.document_name || 'dokument';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download fehlgeschlagen:', err);
      addToast('error', 'Download fehlgeschlagen');
    }
  };

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    try {
      if (useGroupedApi) {
        // Use optimized grouped API
        const response = await apiClient.get('/api/drinks/grouped', {
          params: {
            page: 1,
            limit: 100,
            status: filter === 'open' ? 'open' : undefined
          }
        });

        setGroupedData(response.data.data);
        setEvents(response.data.events);

        // Convert grouped data to flat structure for compatibility
        const flatDrinks: DrinkEntry[] = [];
        const membersSet = new Map<number, Member>();

        response.data.data.forEach((member: GroupedDrinkData) => {
          // Check if member is a guest based on roles
          const isGuest = member.roles?.includes('guest') || member.is_guest === true;

          membersSet.set(member.member_id, {
            mg_nr: member.member_id,
            mitglied_nr: member.member_id.toString(),
            name: member.name,
            vorname: member.vorname,
            email: member.email,
            foto_path: member.foto_path,
            foto_path_updated: member.foto_path_updated,
            is_guest: isGuest
          });

          member.drinks.forEach(drink => {
            flatDrinks.push({
              id: drink.drink_id,
              member_id: member.member_id,
              month: drink.event_id,
              amount: drink.amount,
              paid: drink.paid,
              paid_date: drink.paid_date,
              payment_method: drink.payment_method as 'cash' | 'paypal' | null,
              token: drink.token,
              event_label: drink.event_label
            });
          });
        });

        const sortedMembers = Array.from(membersSet.values()).sort((a, b) =>
          a.vorname.localeCompare(b.vorname, 'de')
        );

        setMembers(sortedMembers);
        setDrinkEntries(flatDrinks);

        // Select first event by default
        if (response.data.events.length > 0 && !selectedEvent) {
          setSelectedEvent(response.data.events[0]);
        }
      } else {
        // Fallback to old API
        const [membersRes, eventsRes, drinksRes] = await Promise.all([
          apiClient.get('/api/members'),
          apiClient.get('/api/events'),
          apiClient.get('/api/drinks')
        ]);

        const sortedMembers = membersRes.data.sort((a: Member, b: Member) =>
          a.vorname.localeCompare(b.vorname, 'de')
        );
        setMembers(sortedMembers);
        const sortedEvents = eventsRes.data.sort((a: Event, b: Event) => b.id - a.id);
        setEvents(sortedEvents);
        setDrinkEntries(drinksRes.data);

        if (eventsRes.data.length > 0 && !selectedEvent) {
          setSelectedEvent(eventsRes.data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to old API if grouped API fails
      if (useGroupedApi) {
        console.log('Grouped API failed, falling back to traditional API');
        setUseGroupedApi(false);
        return fetchData();
      }
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Daten konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Hinweis',
        message: 'Bitte geben Sie einen Event-Namen ein.',
        variant: 'single'
      });
      return;
    }

    try {
      const abrechnungsmonat = `${newEventYear}-${String(newEventMonth).padStart(2, '0')}-01`;
      const response = await apiClient.post('/api/events', {
        beschriftung: newEventName,
        abrechnungsmonat
      });

      setShowNewEventModal(false);
      setNewEventName('');
      setNewEventMonth(new Date().getMonth() + 1);
      setNewEventYear(new Date().getFullYear());

      // Reload all data to ensure consistency
      await fetchData();

      // Select the newly created event
      setSelectedEvent(response.data);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Event wurde erfolgreich erstellt.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Error creating event:', error);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Event konnte nicht erstellt werden.',
        variant: 'single'
      });
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    // Check if event has entries
    const hasEntries = drinkEntries.some(entry => entry.month === eventId);

    if (hasEntries) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Nicht möglich',
        message: 'Event kann nicht gelöscht werden, da bereits Einträge vorhanden sind.',
        variant: 'single'
      });
      return;
    }

    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Event löschen',
      message: 'Möchten Sie dieses Event wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        try {
          // Start delete animation
          setDeletingEventId(eventId);

          // Wait for animation to complete
          setTimeout(async () => {
            await apiClient.delete(`/api/drinks/events/${eventId}`);
            setEvents(events.filter(e => e.id !== eventId));
            setDeletingEventId(null);

            if (selectedEvent?.id === eventId) {
              setSelectedEvent(events[0] || null);
            }

            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Event wurde gelöscht.',
              variant: 'single'
            });
          }, 300); // Animation duration
        } catch (error) {
          console.error('Error deleting event:', error);
          setDeletingEventId(null);
          setConfirmModal({
            visible: true,
            type: 'error',
            title: 'Fehler',
            message: 'Event konnte nicht gelöscht werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  const handleAmountSubmit = async (memberId: number, eventId: number, amount: string) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      addToast('warning', 'Bitte geben Sie einen gültigen Betrag ein.');
      return;
    }

    // Set loading state for this cell
    const cellKey = `${memberId}-${eventId}`;
    setSavingCells(prev => new Set(prev).add(cellKey));

    try {
      const existingEntry = drinkEntries.find(
        e => e.member_id === memberId && e.month === eventId
      );

      let responseData: any;
      if (existingEntry) {
        // Update existing entry - using POST instead of PUT
        // If amount changes, reset paid status to 0
        const shouldResetPaid = existingEntry.amount !== numAmount && existingEntry.paid === 1;
        const response = await apiClient.post('/api/drinks', {
          id: existingEntry.id,
          member_id: memberId,
          month: eventId,
          amount: numAmount,
          paid: shouldResetPaid ? 0 : existingEntry.paid
        });
        responseData = response.data;

        // Handle backend response - only update if success
        if (responseData.success) {
          // Update local state immediately - reset paid status if amount changed
          setDrinkEntries(prev => prev.map(entry =>
            entry.id === existingEntry.id
              ? {
                  ...entry,
                  amount: numAmount,
                  paid: shouldResetPaid ? 0 : existingEntry.paid,
                  paid_date: shouldResetPaid ? undefined : existingEntry.paid_date,
                  payment_method: shouldResetPaid ? undefined : existingEntry.payment_method
                }
              : entry
          ));

          // Update groupedData as well
          setGroupedData(prev => prev.map(member => {
            if (member.member_id === memberId) {
              return {
                ...member,
                drinks: member.drinks.map(drink =>
                  drink.event_id === eventId
                    ? {
                        ...drink,
                        amount: numAmount,
                        paid: shouldResetPaid ? 0 : drink.paid,
                        paid_date: shouldResetPaid ? undefined : drink.paid_date,
                        payment_method: shouldResetPaid ? undefined : drink.payment_method
                      }
                    : drink
                ),
                totals: {
                  ...member.totals,
                  total: member.drinks.reduce((sum, d) =>
                    sum + (d.event_id === eventId ? numAmount : d.amount), 0
                  ),
                  paid: member.drinks.reduce((sum, d) => {
                    if (d.event_id === eventId) {
                      return sum + (shouldResetPaid ? 0 : d.paid * numAmount);
                    }
                    return sum + (d.paid * d.amount);
                  }, 0),
                  open: member.drinks.reduce((sum, d) => {
                    if (d.event_id === eventId) {
                      return sum + (shouldResetPaid ? numAmount : (1 - d.paid) * numAmount);
                    }
                    return sum + ((1 - d.paid) * d.amount);
                  }, 0)
                }
              };
            }
            return member;
          }));
        }
      } else {
        // Create new entry
        const response = await apiClient.post('/api/drinks', {
          member_id: memberId,
          month: eventId,
          amount: numAmount,
          paid: 0
        });
        responseData = response.data;

        // Handle new backend response format
        if (responseData.success && responseData.drinkId) {
          // New format: {success: true, drinkId: 593, updated: false}
          const event = events.find(e => e.id === eventId);
          const newEntry: DrinkEntry = {
            id: responseData.drinkId,
            member_id: memberId,
            month: eventId,
            amount: numAmount,
            paid: 0,
            paid_date: undefined,
            paid_by: undefined,
            payment_method: undefined,
            token: null,
            event_label: event?.beschriftung || `Event ${eventId}`
          };

          setDrinkEntries(prev => [...prev, newEntry]);

          // Update or add to groupedData
          setGroupedData(prev => {
            const memberIndex = prev.findIndex(m => m.member_id === memberId);
            if (memberIndex >= 0) {
              // Member exists in grouped data, add new drink
              const updatedMember = { ...prev[memberIndex] };
              updatedMember.drinks = [...updatedMember.drinks, {
                drink_id: responseData.drinkId,
                event_id: eventId,
                event_label: event?.beschriftung || `Event ${eventId}`,
                amount: numAmount,
                paid: 0,
                paid_date: undefined,
                payment_method: undefined,
                token: null
              }];
              updatedMember.totals = {
                total: updatedMember.totals.total + numAmount,
                paid: updatedMember.totals.paid,
                open: updatedMember.totals.open + numAmount
              };

              const newGroupedData = [...prev];
              newGroupedData[memberIndex] = updatedMember;
              return newGroupedData;
            } else {
              // Member not in grouped data, add them
              const member = members.find(m => m.mg_nr === memberId);
              if (member) {
                return [...prev, {
                  member_id: memberId,
                  name: member.name,
                  vorname: member.vorname,
                  email: member.email,
                  foto_path: member.foto_path,
                  foto_path_updated: member.foto_path_updated,
                  drinks: [{
                    drink_id: responseData.drinkId,
                    event_id: eventId,
                    event_label: event?.beschriftung || `Event ${eventId}`,
                    amount: numAmount,
                    paid: 0,
                    paid_date: undefined,
                    payment_method: undefined,
                    token: null
                  }],
                  totals: {
                    total: numAmount,
                    paid: 0,
                    open: numAmount
                  }
                }];
              }
              return prev;
            }
          });
        } else if (responseData.id) {
          // Old format fallback
          const event = events.find(e => e.id === eventId);
          const newEntry: DrinkEntry = {
            id: responseData.id,
            member_id: memberId,
            month: eventId,
            amount: numAmount,
            paid: responseData.paid || 0,
            paid_date: responseData.paid_date || undefined,
            paid_by: responseData.paid_by || undefined,
            payment_method: responseData.payment_method || undefined,
            token: responseData.token || null,
            event_label: event?.beschriftung || `Event ${eventId}`
          };

          setDrinkEntries(prev => [...prev, newEntry]);
        }
      }

      setEditingEntry(null);
      addToast('success', 'Betrag wurde gespeichert.');
    } catch (error) {
      console.error('Error saving amount:', error);
      addToast('error', 'Betrag konnte nicht gespeichert werden.');
    } finally {
      // Remove loading state
      setSavingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  };

  const handleMarkAsPaid = async (entryId: number, method: 'cash' | 'paypal') => {
    const entry = drinkEntries.find(e => e.id === entryId);
    if (!entry) {
      addToast('error', 'Eintrag nicht gefunden.');
      return;
    }

    // Set loading state for this cell
    const cellKey = `${entry.member_id}-${entry.month}`;
    setSavingCells(prev => new Set(prev).add(cellKey));

    try {
      const paidDate = new Date().toISOString();

      await apiClient.post('/api/drinks', {
        id: entryId,
        member_id: entry.member_id,
        month: entry.month,
        amount: entry.amount,
        paid: 1,
        paid_date: paidDate,
        payment_method: method
      });

      // Optimistic UI update - update the entry immediately
      setDrinkEntries(prev => prev.map(e =>
        e.id === entryId
          ? { ...e, paid: 1, paid_date: paidDate, payment_method: method }
          : e
      ));

      // Update groupedData as well
      setGroupedData(prev => prev.map(member => {
        if (member.member_id === entry.member_id) {
          const updatedDrinks = member.drinks.map(drink =>
            drink.event_id === entry.month
              ? {
                  ...drink,
                  paid: 1,
                  paid_date: paidDate,
                  payment_method: method
                }
              : drink
          );

          // Recalculate totals
          const total = updatedDrinks.reduce((sum, d) => sum + d.amount, 0);
          const paid = updatedDrinks.reduce((sum, d) => sum + (d.paid * d.amount), 0);
          const open = total - paid;

          return {
            ...member,
            drinks: updatedDrinks,
            totals: { total, paid, open }
          };
        }
        return member;
      }));

      // Statistics will be recalculated on next render via calculateStats()
      addToast('success', `Betrag wurde als bezahlt markiert (${method === 'cash' ? 'Bar' : 'PayPal'}).`);
    } catch (error) {
      console.error('Error marking as paid:', error);
      addToast('error', 'Status konnte nicht aktualisiert werden.');
    } finally {
      // Remove loading state
      setSavingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  };

  // Filter members based on selected filter
  const getFilteredMembers = () => {
    let filtered = [...members]; // Create a copy to avoid mutations

    // Guest filter - hide ALL guests when checkbox is not checked
    if (!showGuests) {
      filtered = filtered.filter(member => {
        // Only show non-guests when showGuests is false
        return member.is_guest !== true;
      });
    }
    // If showGuests is true, show all members including guests

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(member =>
        `${member.vorname} ${member.name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter for matrix view
    if (filter !== 'all' && filter !== 'hide_paid_events') {
      switch (filter) {
        case 'open':
          // Only members with unpaid entries
          filtered = filtered.filter(member =>
            drinkEntries.some(e => e.member_id === member.mg_nr && e.paid === 0)
          );
          break;
        case 'with_entries':
          // Only members with any entries
          filtered = filtered.filter(member =>
            drinkEntries.some(e => e.member_id === member.mg_nr)
          );
          break;
      }
    }

    return filtered;
  };

  // Calculate statistics
  const calculateStats = () => {
    const total = drinkEntries.reduce((sum, e) => sum + e.amount, 0);
    const paid = drinkEntries.filter(e => e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
    const open = total - paid;
    const membersWithEntries = new Set(drinkEntries.map(e => e.member_id)).size;

    // Count total members (excluding guests)
    const totalMembers = members.filter(m => !m.is_guest).length;

    return { total, paid, open, members: membersWithEntries, totalMembers };
  };

  // Get visible events (for matrix columns)
  const getVisibleEvents = () => {
    let visibleEvents = events;

    // Filter out fully paid events if filter is active
    if (filter === 'hide_paid_events' || filter === 'open') {
      visibleEvents = visibleEvents.filter(event => {
        const eventEntries = drinkEntries.filter(e => e.month === event.id);
        // Keep event if it has any unpaid entries
        return eventEntries.some(e => e.paid === 0);
      });
    }

    return visibleEvents; // Show all events
  };

  // Check if member has open entries
  const memberHasOpenEntries = (memberId: number) => {
    return drinkEntries.some(e => e.member_id === memberId && e.paid === 0);
  };

  // Calculate member totals
  const getMemberTotals = (memberId: number) => {
    const memberEntries = drinkEntries.filter(e => e.member_id === memberId);
    const total = memberEntries.reduce((sum, e) => sum + e.amount, 0);
    const paid = memberEntries.filter(e => e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
    const open = total - paid;
    return { total, paid, open };
  };

  const stats = calculateStats();
  const filteredMembers = getFilteredMembers();

  const getMemberImageUrl = (member: Member) => {
    if (!member.foto_path) return null;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${member.mg_nr}?size=S&token=${token}&ts=${member.foto_path_updated || Date.now()}`;
  };

  const visibleEvents = getVisibleEvents();

  // Quick Entry Functions
  const handleQuickEntryOpen = () => {
    if (events.length === 0) {
      setConfirmModal({
        visible: true,
        type: 'warning',
        title: 'Hinweis',
        message: 'Bitte erstellen Sie zuerst ein Event.',
        variant: 'single'
      });
      return;
    }
    const eventId = events[0].id;
    setQuickEntryEventId(eventId);
    setQuickEntryIndex(0);

    // Load existing value for first member
    if (members.length > 0) {
      const firstMember = members[0];
      const existingEntry = drinkEntries.find(
        entry => entry.member_id === firstMember.mg_nr && entry.month === eventId
      );
      setQuickEntryValue(existingEntry ? existingEntry.amount.toString() : '');
    } else {
      setQuickEntryValue('');
    }

    setQuickEntryValues({});
    setShowQuickEntryModal(true);
  };

  const handleQuickEntrySubmit = async () => {
    if (!quickEntryEventId) return;

    const currentMember = members[quickEntryIndex];
    if (!currentMember) return;

    // Parse the value (handle both . and , as decimal separator)
    const cleanValue = quickEntryValue.replace(',', '.');
    const numAmount = parseFloat(cleanValue);

    if (!isNaN(numAmount) && numAmount > 0) {
      // Store the value temporarily for display
      setQuickEntryValues(prev => ({ ...prev, [currentMember.mg_nr]: cleanValue }));

      try {
        const existingEntry = drinkEntries.find(
          e => e.member_id === currentMember.mg_nr && e.month === quickEntryEventId
        );

        let responseData: any;
        if (existingEntry) {
          // If amount changes, reset paid status to 0
          const shouldResetPaid = existingEntry.amount !== numAmount && existingEntry.paid === 1;
          const response = await apiClient.post('/api/drinks', {
            id: existingEntry.id,
            member_id: currentMember.mg_nr,
            month: quickEntryEventId,
            amount: numAmount,
            paid: shouldResetPaid ? 0 : existingEntry.paid
          });
          responseData = response.data;

          // Update existing entry immediately - reset paid status if amount changed
          setDrinkEntries(prev => prev.map(entry =>
            entry.id === existingEntry.id
              ? {
                  ...entry,
                  amount: numAmount,
                  paid: shouldResetPaid ? 0 : existingEntry.paid,
                  paid_date: shouldResetPaid ? undefined : existingEntry.paid_date,
                  payment_method: shouldResetPaid ? undefined : existingEntry.payment_method
                }
              : entry
          ));
        } else {
          const response = await apiClient.post('/api/drinks', {
            member_id: currentMember.mg_nr,
            month: quickEntryEventId,
            amount: numAmount,
            paid: 0
          });
          responseData = response.data;

          // Handle backend response format
          let newEntryId: number;
          if (responseData.success && responseData.drinkId) {
            newEntryId = responseData.drinkId;
          } else if (responseData.id) {
            newEntryId = responseData.id;
          } else {
            console.error('No ID in response:', responseData);
            return;
          }

          // Add new entry immediately with data from response
          const event = events.find(e => e.id === quickEntryEventId);
          const newEntry = {
            id: newEntryId,
            member_id: currentMember.mg_nr,
            month: quickEntryEventId,
            amount: numAmount,
            paid: 0,
            paid_date: undefined,
            paid_by: undefined,
            payment_method: undefined,
            token: null,
            event_label: event?.beschriftung || `Event ${quickEntryEventId}`
          };

          setDrinkEntries(prev => [...prev, newEntry]);

          // Also update quickEntryValues to show in the list
          setQuickEntryValues(prev => ({
            ...prev,
            [currentMember.mg_nr]: numAmount.toFixed(2)
          }));
        }

        // Statistics will be recalculated on next render via calculateStats()
      } catch (error) {
        console.error('Error saving quick entry:', error);
        addToast('error', 'Fehler beim Speichern des Betrags');
      }
    }

    // Move to next member
    if (quickEntryIndex < members.length - 1) {
      const nextIndex = quickEntryIndex + 1;
      setQuickEntryIndex(nextIndex);

      // Load existing value for next member
      const nextMember = members[nextIndex];
      const existingEntry = drinkEntries.find(
        entry => entry.member_id === nextMember.mg_nr && entry.month === quickEntryEventId
      );
      setQuickEntryValue(existingEntry ? existingEntry.amount.toString() : '');
    } else {
      // All done - no fetchData needed, we already updated local state
      setShowQuickEntryModal(false);
      addToast('success', 'Alle Beträge wurden gespeichert.');
    }
  };

  const handleQuickEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuickEntrySubmit();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Go back with Shift+Tab
        if (quickEntryIndex > 0) {
          setQuickEntryIndex(quickEntryIndex - 1);
          // Load previous value if exists
          const prevMember = members[quickEntryIndex - 1];
          const existingEntry = drinkEntries.find(
            entry => entry.member_id === prevMember.mg_nr && entry.month === quickEntryEventId
          );
          setQuickEntryValue(existingEntry ? existingEntry.amount.toString() : '');
        }
      } else {
        // Skip to next member without saving
        if (quickEntryIndex < members.length - 1) {
          setQuickEntryIndex(quickEntryIndex + 1);
          // Load next value if exists
          const nextMember = members[quickEntryIndex + 1];
          const existingEntry = drinkEntries.find(
            entry => entry.member_id === nextMember.mg_nr && entry.month === quickEntryEventId
          );
          setQuickEntryValue(existingEntry ? existingEntry.amount.toString() : '');
        }
      }
    } else if (e.key === 'Escape') {
      setShowQuickEntryModal(false);
    }
  };

  // ===== Derived data for the new design =====
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const paidPct = stats.total > 0 ? Math.round((stats.paid / stats.total) * 100) : 0;
  const ringDeg = (paidPct / 100) * 360;

  const memberSummaries = useMemo(() => {
    return filteredMembers.map(m => {
      const entries = drinkEntries.filter(e => e.member_id === m.mg_nr);
      const openEntries = entries.filter(e => e.paid === 0);
      const open = openEntries.reduce((s, e) => s + e.amount, 0);
      const total = entries.reduce((s, e) => s + e.amount, 0);
      const paid = total - open;
      const paidEntries = entries.filter(e => e.paid === 1);
      const lastPaidDate = paidEntries
        .map(e => e.paid_date ? new Date(e.paid_date) : null)
        .filter((d): d is Date => d !== null)
        .sort((a, b) => b.getTime() - a.getTime())[0];

      // ältester offener Eintrag (nach Event)
      const oldestOpen = openEntries
        .map(e => events.find(ev => ev.id === e.month))
        .filter((ev): ev is Event => Boolean(ev))
        .filter(ev => ev.abrechnungsmonat)
        .sort((a, b) => new Date(a.abrechnungsmonat!).getTime() - new Date(b.abrechnungsmonat!).getTime())[0];

      const ageDays = oldestOpen?.abrechnungsmonat
        ? Math.floor((Date.now() - new Date(oldestOpen.abrechnungsmonat).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      let stage: 'paid' | 'open' | 'mahnung1' | 'mahnung2' = 'paid';
      if (open > 0) {
        if (ageDays > 60) stage = 'mahnung2';
        else if (ageDays > 30) stage = 'mahnung1';
        else stage = 'open';
      }

      return { member: m, entries, openEntries, open, paid, total, lastPaidDate, ageDays, stage };
    });
  }, [filteredMembers, drinkEntries, events]);

  const membersOpen = memberSummaries.filter(s => s.open > 0);
  const membersOver60 = memberSummaries.filter(s => s.ageDays > 60 && s.open > 0);
  const lastPaymentDate = drinkEntries
    .filter(e => e.paid === 1 && e.paid_date)
    .map(e => new Date(e.paid_date!))
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const eventSummaries = useMemo(() => {
    return events.map(ev => {
      const entries = drinkEntries.filter(e => e.month === ev.id);
      const total = entries.reduce((s, e) => s + e.amount, 0);
      const paid = entries.filter(e => e.paid === 1).reduce((s, e) => s + e.amount, 0);
      const open = total - paid;
      const memberCount = new Set(entries.map(e => e.member_id)).size;
      const openCount = entries.filter(e => e.paid === 0).length;
      const paidCount = entries.filter(e => e.paid === 1).length;
      return { event: ev, entries, total, paid, open, memberCount, openCount, paidCount };
    });
  }, [events, drinkEntries]);

  // Saison-Verlauf: Letzte 12 Monate
  const monthlyTimeline = useMemo(() => {
    const now = new Date();
    const months: Array<{ key: string; label: string; year: number; monthNum: number; total: number; paid: number; open: number; events: number }> = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({
        key,
        label: d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', ''),
        year: d.getFullYear(),
        monthNum: d.getMonth() + 1,
        total: 0, paid: 0, open: 0, events: 0,
      });
    }
    events.forEach(ev => {
      if (!ev.abrechnungsmonat) return;
      const d = new Date(ev.abrechnungsmonat);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const slot = months.find(m => m.key === key);
      if (!slot) return;
      slot.events += 1;
      const evEntries = drinkEntries.filter(e => e.month === ev.id);
      slot.total += evEntries.reduce((s, e) => s + e.amount, 0);
      slot.paid += evEntries.filter(e => e.paid === 1).reduce((s, e) => s + e.amount, 0);
    });
    months.forEach(m => { m.open = m.total - m.paid; });
    return months;
  }, [events, drinkEntries]);

  if (loading) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6">
          <div className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
            <div className="h-8 w-48 bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-72 bg-slate-700/40 rounded" />
          </div>
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl p-5 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <div className="h-5 w-2/3 bg-slate-700/60 rounded mb-2" />
              <div className="h-4 w-1/3 bg-slate-700/40 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ===== Helpers for the new design =====
  const initialsOf = (m: Member) => `${(m.vorname?.[0] || '').toUpperCase()}${(m.name?.[0] || '').toUpperCase()}`;

  const renderMemberAvatar = (m: Member, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = size === 'sm' ? 'w-9 h-9 text-xs' : size === 'lg' ? 'w-12 h-12 text-sm' : 'w-10 h-10 text-xs';
    const url = getMemberImageUrl(m);
    if (url && !imageErrors[`member-${m.mg_nr}`]) {
      return (
        <img
          src={url}
          alt={`${m.vorname} ${m.name}`}
          className={`border border-gray-200 dark:border-white/10 ${`${sizeClasses} rounded-xl object-cover shrink-0`}`}
          onError={() => setImageErrors(prev => ({ ...prev, [`member-${m.mg_nr}`]: true }))}
        />
      );
    }
    return (
      <div
        className={`border border-gray-200 dark:border-white/10 ${`${sizeClasses} rounded-xl flex items-center justify-center font-black text-white shrink-0`}`}
        style={{ background: 'rgba(255,255,255,0.08)' }}
      >
        {initialsOf(m)}
      </div>
    );
  };

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            Verzehrkosten · Verwaltung · Stand {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight mb-4">
            Theken-<span className="text-amber-600 dark:text-amber-400">Zentrale.</span>
          </h1>
          <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
            {events.length === 0 ? (
              <>Noch keine Events angelegt. Lege jetzt deinen ersten <span className="font-bold text-gray-900 dark:text-white">Vereinsabend</span> an, um Beträge zu erfassen.</>
            ) : stats.open > 0 ? (
              <>
                Aktuell stehen{' '}
                <span className="font-bold text-amber-700 dark:text-amber-300">{formatCurrency(stats.open)}</span>{' '}
                offen bei{' '}
                <span className="font-bold text-gray-900 dark:text-white">{membersOpen.length} {membersOpen.length === 1 ? 'Mitglied' : 'Mitgliedern'}</span>
                {membersOver60.length > 0 && (
                  <>, <span className="font-bold text-rose-700 dark:text-rose-300">{membersOver60.length} davon älter 60 Tage</span></>
                )}
                {lastPaymentDate && (
                  <>. Letzter Ausgleich:{' '}
                    <span className="font-bold text-gray-900 dark:text-white">
                      {lastPaymentDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </>
                )}
                .
              </>
            ) : (
              <>Saubere Sache — <span className="font-bold text-primary-700 dark:text-primary-300">alle Beiträge ausgeglichen</span> über {events.length} {events.length === 1 ? 'Event' : 'Events'} mit insgesamt <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(stats.paid)}</span>.</>
            )}
          </p>
        </section>

        {/* WELCOME / EMPTY-STATE WHEN NO EVENTS */}
        {events.length === 0 ? (
          <section className="pb-6">
            <div
              className="rounded-3xl p-8 sm:p-12 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div
                className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
              >
                <CalendarIcon className="h-10 w-10 text-amber-700 dark:text-amber-300" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                Willkommen bei den Verzehrkosten
              </h2>
              <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-xl mx-auto">
                Lege deinen ersten Vereinsabend an. Anschließend kannst du Beträge erfassen, Zahlungen abhaken und Listen exportieren.
              </p>
              <button
                onClick={() => setShowNewEventModal(true)}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition"
                style={{ background: '#10b981', color: '#0b1a14' }}
              >
                <PlusIcon className="h-5 w-5" />
                Erstes Event erstellen
              </button>
            </div>
          </section>
        ) : (
          <>
            {/* ACTION HERO */}
            <section className="pb-6">
              <article
                className="relative rounded-3xl overflow-hidden p-6 sm:p-7 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                <div
                  className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none blur-3xl"
                  style={{ background: stats.open > 0 ? 'rgba(245,158,11,0.18)' : 'rgba(16,185,129,0.18)' }}
                />
                <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className="flex items-center gap-4">
                    <div
                      className="relative w-28 h-28 rounded-full shrink-0"
                      style={{
                        background: `conic-gradient(from 0deg, #34d399 0deg ${ringDeg}deg, #f59e0b ${ringDeg}deg 360deg)`,
                        padding: 5,
                      }}
                    >
                      <div
                        className="w-full h-full rounded-full flex flex-col items-center justify-center bg-white dark:bg-[#1c2432]"
                        style={{ border: '1px solid #1e293b'  }}
                      >
                        <div className="text-[9px] uppercase tracking-widest text-gray-600 dark:text-slate-400">Quote</div>
                        <div className="text-3xl font-black text-gray-900 dark:text-white leading-none tabular-nums">{paidPct}</div>
                        <div className="text-[10px] text-primary-600 dark:text-primary-400 font-semibold mt-0.5">% bezahlt</div>
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {stats.open > 0 ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/40 bg-amber-500/15 text-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          Offene Beiträge
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-500/40 bg-primary-500/15 text-primary-200">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Alles ausgeglichen
                        </span>
                      )}
                      {membersOver60.length > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-rose-500/40 bg-rose-500/15 text-rose-200">
                          {membersOver60.length} {membersOver60.length === 1 ? 'Mahnstufe 2 fällig' : 'Mahnstufen 2 fällig'}
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                      {stats.open > 0
                        ? membersOpen.length === 1
                          ? `${membersOpen[0]?.member.vorname || 'Eine Person'} hat noch offene Beiträge`
                          : `${membersOpen.length} Mitglieder mit offenen Beiträgen`
                        : 'Saison komplett ausgeglichen'}
                    </h3>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1.5 max-w-xl">
                      {stats.open > 0
                        ? <>Insgesamt <span className="font-bold text-amber-700 dark:text-amber-300">{formatCurrency(stats.open)}</span> offen aus <span className="font-bold text-gray-900 dark:text-white">{events.length} {events.length === 1 ? 'Event' : 'Events'}</span>. Schick eine Sammel-Erinnerung — die meisten zahlen innerhalb 14 Tagen.</>
                        : <>Du hast <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(stats.paid)}</span> über {events.length} {events.length === 1 ? 'Event' : 'Events'} eingenommen.</>
                      }
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:flex-col lg:w-48">
                    <div
                      className="flex items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5 flex-1"
                      style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Offen</div>
                      <div className="text-base sm:text-sm lg:text-base font-black text-amber-200 tabular-nums whitespace-nowrap">{formatCurrency(stats.open)}</div>
                    </div>
                    <div
                      className="flex items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5 flex-1"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Bezahlt</div>
                      <div className="text-base sm:text-sm lg:text-base font-black text-primary-200 tabular-nums whitespace-nowrap">{formatCurrency(stats.paid)}</div>
                    </div>
                    <div
                      className="flex items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5 flex-1 bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08]"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-gray-600 dark:text-slate-400 font-bold">Soll</div>
                      <div className="text-base sm:text-sm lg:text-base font-black text-gray-900 dark:text-white tabular-nums whitespace-nowrap">{formatCurrency(stats.total)}</div>
                    </div>
                  </div>
                </div>

                {/* Progress + actions */}
                <div className="relative mt-5">
                  <div className="h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/5" >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${paidPct}%`, background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500 dark:text-slate-500">
                    <span>0 €</span>
                    <span className="text-gray-700 dark:text-slate-300">{formatCurrency(stats.paid)} bezahlt</span>
                    <span>{formatCurrency(stats.total)}</span>
                  </div>
                </div>
              </article>
            </section>

            {/* SAISON-VERLAUF: 12 Monate */}
            <section className="pb-6">
              <article
                className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Saison-Verlauf</div>
                    <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      Letzte 12 Monate · {events.length} {events.length === 1 ? 'Event' : 'Events'}
                    </h4>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-600 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary-400" />Bezahlt</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" />Teilweise</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-400" />Offen</span>
                  </div>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
                  {monthlyTimeline.map(m => {
                    const fullyPaid = m.total > 0 && m.open === 0;
                    const partlyPaid = m.total > 0 && m.paid > 0 && m.open > 0;
                    const fullyOpen = m.total > 0 && m.paid === 0;
                    const empty = m.total === 0;

                    const bg = fullyPaid
                      ? 'rgba(16,185,129,0.10)'
                      : partlyPaid
                      ? 'rgba(245,158,11,0.10)'
                      : fullyOpen
                      ? 'rgba(244,63,94,0.10)'
                      : 'rgba(255,255,255,0.03)';
                    const border = fullyPaid
                      ? '1px solid rgba(16,185,129,0.30)'
                      : partlyPaid
                      ? '1px solid rgba(245,158,11,0.30)'
                      : fullyOpen
                      ? '1px solid rgba(244,63,94,0.30)'
                      : '1px solid rgba(255,255,255,0.06)';
                    const labelColor = fullyPaid
                      ? 'text-primary-300'
                      : partlyPaid
                      ? 'text-amber-300'
                      : fullyOpen
                      ? 'text-rose-300'
                      : 'text-slate-500';
                    return (
                      <div
                        key={m.key}
                        className="flex flex-col items-center gap-1 p-2 rounded-xl"
                        style={{ background: bg, border }}
                      >
                        <span className={`text-[9px] uppercase tracking-widest font-bold ${labelColor}`}>
                          {m.label}
                        </span>
                        <span className="text-[10px] text-gray-500 dark:text-slate-500">{m.year}</span>
                        <span className="text-sm font-black text-gray-900 dark:text-white tabular-nums leading-none">
                          {empty ? '–' : m.total.toFixed(0)}
                        </span>
                        {!empty && (
                          <span className={`text-[9px] ${labelColor}`}>
                            {fullyPaid ? '✓ bezahlt' : partlyPaid ? `${m.open.toFixed(0)} € offen` : 'offen'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </article>
            </section>

            {/* QUICK ACTIONS */}
            <section className="pb-6">
              {/* Mobile: kompakte 4-Icon-Reihe ohne Card-Wrapper */}
              <div className="flex gap-2 sm:hidden">
                <button
                  onClick={() => setShowNewEventModal(true)}
                  className="flex-1 flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl transition"
                  style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.20)' }}
                >
                  <PlusIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                  <div className="text-[10px] font-semibold text-gray-800 dark:text-slate-200">Event</div>
                </button>
                <button
                  onClick={handleQuickEntryOpen}
                  className="flex-1 flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl transition"
                  style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.20)' }}
                >
                  <CurrencyEuroIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                  <div className="text-[10px] font-semibold text-gray-800 dark:text-slate-200">Eingabe</div>
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex-1 flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl transition"
                  style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)' }}
                >
                  <DocumentArrowDownIcon className="w-5 h-5 text-blue-700 dark:text-blue-300" />
                  <div className="text-[10px] font-semibold text-gray-800 dark:text-slate-200">Export</div>
                </button>
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="flex-1 flex flex-col items-center gap-1.5 px-2 py-3 rounded-2xl transition"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <QuestionMarkCircleIcon className="w-5 h-5 text-gray-700 dark:text-slate-300" />
                  <div className="text-[10px] font-semibold text-gray-800 dark:text-slate-200">Hilfe</div>
                </button>
              </div>

              {/* Desktop: ausführliche Card mit Title + 4 Buttons */}
              <article
                className="hidden sm:block rounded-3xl p-5 sm:p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
              >
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Schnellzugriff</div>
                    <h4 className="text-lg md:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Aktionen</h4>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <button
                    onClick={() => setShowNewEventModal(true)}
                    className="group flex items-center gap-3 px-4 py-3.5 min-h-[68px] rounded-2xl text-left bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/30 hover:border-emerald-500/50 active:scale-[0.98] transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-500/20 border border-emerald-500/40">
                      <PlusIcon className="w-5 h-5 text-emerald-700 dark:text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Event anlegen</div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">Neuer Vereinsabend</div>
                    </div>
                  </button>
                  <button
                    onClick={handleQuickEntryOpen}
                    className="group flex items-center gap-3 px-4 py-3.5 min-h-[68px] rounded-2xl text-left bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/30 hover:border-amber-500/50 active:scale-[0.98] transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-amber-500/20 border border-amber-500/40">
                      <CurrencyEuroIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Schnelleingabe</div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">Beträge erfassen</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="group flex items-center gap-3 px-4 py-3.5 min-h-[68px] rounded-2xl text-left bg-blue-500/10 hover:bg-blue-500/15 border border-blue-500/30 hover:border-blue-500/50 active:scale-[0.98] transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-500/20 border border-blue-500/40">
                      <DocumentArrowDownIcon className="w-5 h-5 text-blue-700 dark:text-blue-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Export</div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">PDF / Excel</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setShowHelpModal(true)}
                    className="group flex items-center gap-3 px-4 py-3.5 min-h-[68px] rounded-2xl text-left bg-gray-50 hover:bg-gray-100 dark:bg-slate-900/60 dark:hover:bg-slate-900/80 border border-gray-200 dark:border-slate-700 active:scale-[0.98] transition"
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10">
                      <QuestionMarkCircleIcon className="w-5 h-5 text-gray-700 dark:text-slate-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Hilfe</div>
                      <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">So funktioniert's</div>
                    </div>
                  </button>
                </div>
              </article>
            </section>

            {/* TABS + TOOLBAR */}
            <section className="pt-2 pb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div
                  className="inline-flex p-1 rounded-2xl overflow-x-auto bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  {([
                    { key: 'overview' as const, label: 'Übersicht', count: null as number | null },
                    { key: 'members' as const, label: 'Pro Mitglied', count: filteredMembers.length },
                    { key: 'events' as const, label: 'Pro Event', count: events.length },
                    { key: 'history' as const, label: 'Verlauf', count: null },
                  ]).map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition whitespace-nowrap ${
                        activeTab === tab.key
                          ? 'bg-amber-500 text-slate-950'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {tab.label}
                      {tab.count !== null && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                            activeTab === tab.key
                              ? 'bg-slate-950/30 text-slate-950'
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-2.5 text-gray-500 dark:text-slate-500" />
                    <input
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Mitglied / Event suchen…"
                      className="pl-9 pr-9 py-2 rounded-xl text-sm text-gray-800 dark:text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/40 w-56 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                      style={{ colorScheme: 'dark'  }}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1.5 p-1 rounded-md hover:bg-white/10"
                      >
                        <XMarkIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => setShowGuests(!showGuests)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                      showGuests
                        ? 'bg-amber-500/15 border border-amber-500/40 text-amber-200'
                        : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <UserPlusIcon className="w-3.5 h-3.5" />
                    Gäste
                  </button>
                </div>
              </div>

              {/* Filter chips */}
              <div className="mt-3 flex flex-wrap gap-2">
                {([
                  { key: 'all' as const, label: 'Alle' },
                  { key: 'open' as const, label: 'Nur Unbezahlte' },
                  { key: 'with_entries' as const, label: 'Mit Einträgen' },
                  { key: 'hide_paid_events' as const, label: 'Bezahlte Events ausblenden' },
                ]).map(chip => (
                  <button
                    key={chip.key}
                    onClick={() => setFilter(chip.key)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition ${
                      filter === chip.key
                        ? 'bg-amber-500 text-slate-950'
                        : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </section>

            {/* OVERVIEW TAB: Matrix table */}
            {activeTab === 'overview' && (
              <section className="pb-6">
                <article
                  className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                >
                  <div
                    className="px-5 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap"
                    style={{ borderBottom: '1px solid #1e293b', background: 'rgba(255,255,255,0.015)' }}
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Übersicht</div>
                      <div className="text-base font-black text-gray-900 dark:text-white">
                        {filteredMembers.length} {filteredMembers.length === 1 ? 'Mitglied' : 'Mitglieder'} · {visibleEvents.length} Events
                      </div>
                    </div>
                    <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                      {formatCurrency(stats.open)} offen
                    </span>
                  </div>

                  {/* Desktop matrix */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ borderBottom: '1px solid #1e293b' }}>
                        <tr>
                          <th
                            className="sticky left-0 z-10 text-left py-3 px-4 text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-slate-500 min-w-[220px] bg-white dark:bg-[#1c2432]"
                            style={{ borderRight: '1px solid #1e293b'  }}
                          >
                            Mitglied
                          </th>
                          {visibleEvents.map(event => (
                            <th
                              key={event.id}
                              className={`text-center py-3 px-2 text-[11px] font-semibold text-slate-300 min-w-[140px] transition-all duration-300 ${
                                deletingEventId === event.id ? 'opacity-0 scale-x-0 w-0 min-w-0 px-0' : 'opacity-100 scale-x-100'
                              }`}
                              style={{ borderRight: '1px solid #1e293b' }}
                            >
                              <div className="flex flex-col items-center">
                                <div className="flex items-baseline gap-1.5">
                                  <span className="truncate max-w-[120px] text-gray-900 dark:text-white" title={event.beschriftung}>
                                    {event.beschriftung}
                                  </span>
                                  {event.abrechnungsmonat && (
                                    <span className="text-[10px] font-normal text-gray-500 dark:text-slate-500">
                                      {new Date(event.abrechnungsmonat).getFullYear()}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-0.5 mt-1">
                                  {event.document_id ? (
                                    <button
                                      onClick={() => setDocMenuEventId(event.id)}
                                      className="p-1 text-blue-700 dark:text-blue-300 hover:bg-blue-500/10 rounded transition-colors"
                                      title={event.document_name || 'Dokument'}
                                    >
                                      <PaperClipIcon className="h-3.5 w-3.5" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => openDocPicker(event.id)}
                                      className="p-1 text-gray-500 dark:text-slate-500 hover:text-blue-300 hover:bg-white/5 rounded transition-colors"
                                      title="Dokument verknüpfen"
                                    >
                                      <PaperClipIcon className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  {!drinkEntries.some(e => e.month === event.id) && (
                                    <button
                                      onClick={() => handleDeleteEvent(event.id)}
                                      className="p-1 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 rounded transition-colors"
                                      title="Event löschen"
                                    >
                                      <TrashIcon className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMembers.map(member => {
                          const hasOpen = memberHasOpenEntries(member.mg_nr);
                          const totals = getMemberTotals(member.mg_nr);
                          return (
                            <tr key={member.mg_nr} style={{ borderBottom: '1px solid #1e293b' }} className="transition-colors hover:bg-white/[0.02]">
                              <td
                                className="sticky left-0 z-10 py-3 px-4"
                                style={{
                                  background: hasOpen ? 'rgba(244,63,94,0.06)' : '#1c2432',
                                  borderRight: '1px solid #1e293b',
                                  borderLeft: hasOpen ? '3px solid #f43f5e' : '3px solid transparent',
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {renderMemberAvatar(member, 'sm')}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-bold text-sm text-gray-900 dark:text-white truncate">
                                        {member.vorname} {member.name}
                                      </p>
                                      {member.is_guest && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                                          <UserPlusIcon className="h-3 w-3" />
                                          Gast
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-gray-500 dark:text-slate-500 mt-0.5">
                                      #{member.mitglied_nr}
                                      {totals.open > 0 && (
                                        <>
                                          <span className="mx-1.5 text-slate-700">·</span>
                                          <span className="font-semibold text-amber-700 dark:text-amber-300 tabular-nums">
                                            {formatCurrency(totals.open)} offen
                                          </span>
                                        </>
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {visibleEvents.map(event => {
                                const entry = drinkEntries.find(
                                  e => e.member_id === member.mg_nr && e.month === event.id
                                );
                                const isEditing = editingEntry?.memberId === member.mg_nr && editingEntry?.eventId === event.id;
                                const cellKey = `${member.mg_nr}-${event.id}`;
                                const isLoading = savingCells.has(cellKey);

                                return (
                                  <td
                                    key={event.id}
                                    className={`py-3 px-2 text-center transition-all duration-300 ${
                                      deletingEventId === event.id ? 'opacity-0 scale-x-0 w-0 min-w-0 px-0' : 'opacity-100 scale-x-100'
                                    }`}
                                    style={{ borderRight: '1px solid #1e293b' }}
                                  >
                                    {isEditing ? (
                                      <div className="flex items-center justify-center gap-1">
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={editingEntry.amount}
                                          onChange={(e) => setEditingEntry({ ...editingEntry, amount: e.target.value })}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleAmountSubmit(member.mg_nr, event.id, editingEntry.amount);
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault();
                                              setEditingEntry(null);
                                            }
                                          }}
                                          className="w-16 px-1 py-1 text-sm rounded text-gray-900 dark:text-white"
                                          style={{ background: 'rgba(15,23,42,0.8)', border: '2px solid rgba(245,158,11,0.5)', colorScheme: 'dark' }}
                                          autoFocus
                                          disabled={isLoading}
                                        />
                                        <button
                                          onClick={() => handleAmountSubmit(member.mg_nr, event.id, editingEntry.amount)}
                                          className="p-0.5 text-primary-700 dark:text-primary-300 hover:bg-primary-500/10 rounded disabled:opacity-50"
                                          disabled={isLoading}
                                        >
                                          <CheckCircleIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() => setEditingEntry(null)}
                                          className="p-0.5 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 rounded disabled:opacity-50"
                                          disabled={isLoading}
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      <div
                                        onDoubleClick={() => !isLoading && setEditingEntry({
                                          memberId: member.mg_nr,
                                          eventId: event.id,
                                          amount: entry?.amount.toString() || ''
                                        })}
                                        className={`cursor-pointer min-h-[36px] flex items-center justify-center hover:bg-white/[0.04] rounded transition-colors p-1 relative ${
                                          isLoading ? 'pointer-events-none' : ''
                                        }`}
                                      >
                                        {isLoading && (
                                          <div className="absolute inset-0 bg-slate-900/70 rounded flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-400 border-t-transparent"></div>
                                          </div>
                                        )}
                                        {entry ? (
                                          <div className={`flex flex-col items-center gap-1 p-1 rounded ${isLoading ? 'opacity-50' : ''}`}>
                                            <p className={`font-bold text-sm tabular-nums ${entry.paid ? 'text-primary-300' : 'text-amber-300'}`}>
                                              €{entry.amount.toFixed(2)}
                                            </p>
                                            {entry.paid ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircleIcon className="h-3 w-3 text-primary-600 dark:text-primary-400" />
                                                <span className="text-[10px] text-primary-700 dark:text-primary-300">
                                                  {entry.payment_method === 'cash' ? 'Bar' : 'PayPal'}
                                                </span>
                                              </div>
                                            ) : (
                                              <div className="flex gap-1">
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); handleMarkAsPaid(entry.id, 'cash'); }}
                                                  disabled={isLoading}
                                                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold transition disabled:opacity-50"
                                                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', color: '#86efac' }}
                                                >
                                                  Bar
                                                </button>
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); handleMarkAsPaid(entry.id, 'paypal'); }}
                                                  disabled={isLoading}
                                                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold transition disabled:opacity-50"
                                                  style={{ background: 'rgba(0,112,243,0.15)', border: '1px solid rgba(0,112,243,0.35)', color: '#93c5fd' }}
                                                >
                                                  PP
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-slate-700 text-lg leading-none">–</span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        {/* Totals row */}
                        <tr style={{ borderTop: '2px solid #334155', background: 'rgba(255,255,255,0.02)' }}>
                          <td
                            className="sticky left-0 z-10 py-4 px-4"
                            style={{ background: 'rgba(28,36,50,0.95)', borderRight: '1px solid #1e293b' }}
                          >
                            <div className="flex items-center gap-2">
                              <CurrencyEuroIcon className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                              <span className="text-sm font-black text-gray-900 dark:text-white">Gesamt pro Event</span>
                            </div>
                          </td>
                          {visibleEvents.map(event => {
                            const eventTotal = drinkEntries.filter(e => e.month === event.id).reduce((sum, e) => sum + e.amount, 0);
                            const eventPaid = drinkEntries.filter(e => e.month === event.id && e.paid === 1).reduce((sum, e) => sum + e.amount, 0);
                            const eventOpen = eventTotal - eventPaid;
                            return (
                              <td
                                key={event.id}
                                className={`py-4 px-2 text-center transition-all duration-300 ${
                                  deletingEventId === event.id ? 'opacity-0 scale-x-0 w-0 min-w-0 px-0' : 'opacity-100 scale-x-100'
                                }`}
                                style={{ borderRight: '1px solid #1e293b' }}
                              >
                                <div className="flex flex-col items-center">
                                  <p className="text-base font-black text-gray-900 dark:text-white tabular-nums">€{eventTotal.toFixed(2)}</p>
                                  <span className="text-[10px] text-primary-700 dark:text-primary-300 mt-0.5">Bezahlt €{eventPaid.toFixed(2)}</span>
                                  {eventOpen > 0 && (
                                    <span className="text-[10px] text-amber-700 dark:text-amber-300">Offen €{eventOpen.toFixed(2)}</span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile compact list */}
                  <div className="md:hidden divide-y" style={{ borderColor: '#1e293b' }}>
                    {filteredMembers.map(member => {
                      const memberEntries = drinkEntries.filter(e => e.member_id === member.mg_nr);
                      const hasOpen = memberHasOpenEntries(member.mg_nr);
                      const totals = getMemberTotals(member.mg_nr);
                      return (
                        <div key={member.mg_nr} className="px-4 py-3" style={{ borderBottom: '1px solid #1e293b' }}>
                          <div className="flex items-center gap-3 mb-2">
                            {renderMemberAvatar(member, 'sm')}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-bold text-sm text-gray-900 dark:text-white truncate">
                                  {member.vorname} {member.name}
                                </p>
                                {member.is_guest && (
                                  <span className="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                                    Gast
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-gray-500 dark:text-slate-500">#{member.mitglied_nr}</p>
                            </div>
                            <div className="text-right shrink-0">
                              {totals.total > 0 && (
                                hasOpen ? (
                                  <p className="text-sm font-black text-amber-700 dark:text-amber-300 tabular-nums">{formatCurrency(totals.open)}</p>
                                ) : (
                                  <p className="text-xs font-semibold text-primary-700 dark:text-primary-300">Bezahlt</p>
                                )
                              )}
                            </div>
                          </div>
                          {memberEntries.length > 0 ? (
                            <div className="space-y-1.5">
                              {visibleEvents.map(event => {
                                const entry = memberEntries.find(e => e.month === event.id);
                                if (!entry) return null;
                                if ((filter === 'open' || filter === 'hide_paid_events') && entry.paid) return null;
                                return (
                                  <div
                                    key={event.id}
                                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl transition-all duration-300 overflow-hidden ${
                                      deletingEventId === event.id ? 'opacity-0 max-h-0 py-0' : 'opacity-100 max-h-20'
                                    }`}
                                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}
                                  >
                                    <span className="text-[12px] text-gray-700 dark:text-slate-300 truncate flex-1">
                                      {event.beschriftung}
                                      {event.abrechnungsmonat && (
                                        <span className="ml-1 text-[10px] text-gray-500 dark:text-slate-500">
                                          {new Date(event.abrechnungsmonat).getFullYear()}
                                        </span>
                                      )}
                                    </span>
                                    <span className={`text-[13px] font-bold tabular-nums shrink-0 ${entry.paid ? 'text-primary-300' : 'text-amber-300'}`}>
                                      €{entry.amount.toFixed(2)}
                                    </span>
                                    {entry.paid ? (
                                      <span className="flex items-center gap-0.5 text-[10px] text-primary-700 dark:text-primary-300 shrink-0">
                                        <CheckCircleIcon className="h-3.5 w-3.5" />
                                        {entry.payment_method === 'cash' ? 'Bar' : 'PP'}
                                      </span>
                                    ) : (
                                      <div className="flex gap-1 shrink-0">
                                        <button
                                          onClick={() => handleMarkAsPaid(entry.id, 'cash')}
                                          className="px-2 py-0.5 rounded text-[10px] font-semibold"
                                          style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', color: '#86efac' }}
                                        >
                                          Bar
                                        </button>
                                        <button
                                          onClick={() => handleMarkAsPaid(entry.id, 'paypal')}
                                          className="px-2 py-0.5 rounded text-[10px] font-semibold"
                                          style={{ background: 'rgba(0,112,243,0.15)', border: '1px solid rgba(0,112,243,0.35)', color: '#93c5fd' }}
                                        >
                                          PayPal
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-[11px] text-gray-500 dark:text-slate-500 italic">Keine Einträge</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </article>
              </section>
            )}

            {/* PER MEMBER TAB */}
            {activeTab === 'members' && (
              <section className="pb-6">
                <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold">
                      Pro Mitglied · offene Beiträge
                    </div>
                    <h3 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                      {membersOpen.length} {membersOpen.length === 1 ? 'Mitglied' : 'Mitglieder'} mit offenen Beiträgen
                    </h3>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                    {formatCurrency(stats.open)} gesamt
                  </span>
                </div>

                <article
                  className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                >
                  <div className="divide-y" style={{ borderColor: '#1e293b' }}>
                    {(membersOpen.length === 0 ? memberSummaries : membersOpen).map(s => {
                      const m = s.member;
                      const stageColor = s.stage === 'mahnung2'
                        ? { text: 'text-rose-300', border: 'border-rose-500/30', bg: 'bg-rose-500/10' }
                        : s.stage === 'mahnung1'
                        ? { text: 'text-rose-300', border: 'border-rose-500/30', bg: 'bg-rose-500/10' }
                        : s.stage === 'open'
                        ? { text: 'text-amber-300', border: 'border-amber-500/30', bg: 'bg-amber-500/10' }
                        : { text: 'text-primary-300', border: 'border-primary-500/30', bg: 'bg-primary-500/10' };
                      const stageLabel = s.stage === 'mahnung2'
                        ? 'Älter 60 Tage'
                        : s.stage === 'mahnung1'
                        ? 'Mahnstufe 1'
                        : s.stage === 'open'
                        ? 'Offen'
                        : 'Bezahlt';
                      return (
                        <div
                          key={m.mg_nr}
                          className="px-5 md:px-6 py-4 grid grid-cols-[1fr_auto] md:grid-cols-[2.4fr_1fr_1fr_1.2fr_auto] gap-4 items-center transition"
                          style={{ borderBottom: '1px solid #1e293b' }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {renderMemberAvatar(m, 'md')}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${stageColor.text} ${stageColor.border} ${stageColor.bg}`}>
                                  {s.stage !== 'paid' && <span className={`w-1.5 h-1.5 rounded-full ${s.stage === 'open' ? 'bg-amber-400' : 'bg-rose-400'} animate-pulse`} />}
                                  {stageLabel}
                                </span>
                                {m.is_guest && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                    Gast
                                  </span>
                                )}
                              </div>
                              <div className="text-sm md:text-base font-bold text-gray-900 dark:text-white truncate">
                                {m.vorname} {m.name}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                                {m.email || '—'} · #{m.mitglied_nr}
                              </div>
                            </div>
                          </div>
                          <div className="hidden md:block text-right">
                            <div className={`text-lg font-black tabular-nums ${s.open > 0 ? 'text-amber-300' : 'text-primary-300'}`}>
                              {s.openEntries.length || s.entries.length}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-slate-500">{s.open > 0 ? 'offene' : 'bezahlte'} Abende</div>
                          </div>
                          <div className="hidden md:block text-right">
                            <div className="flex items-baseline gap-1 justify-end">
                              <span className={`text-lg font-black tabular-nums ${s.open > 0 ? 'text-amber-300' : 'text-primary-300'}`}>
                                {(s.open > 0 ? s.open : s.paid).toFixed(2).replace('.', ',')}
                              </span>
                              <span className="text-[11px] text-gray-600 dark:text-slate-400">€</span>
                            </div>
                          </div>
                          <div className="hidden md:block">
                            {s.lastPaidDate ? (
                              <>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {s.lastPaidDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-slate-500">letzte Zahlung</div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm font-semibold text-gray-700 dark:text-slate-300">—</div>
                                <div className="text-[11px] text-gray-500 dark:text-slate-500">Noch keine Zahlung</div>
                              </>
                            )}
                          </div>
                          <div className="md:hidden text-right shrink-0">
                            <div className={`text-base font-black tabular-nums ${s.open > 0 ? 'text-amber-300' : 'text-primary-300'}`}>
                              {formatCurrency(s.open > 0 ? s.open : s.paid)}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-slate-500">
                              {s.openEntries.length || s.entries.length} {s.open > 0 ? 'offen' : 'bezahlt'}
                            </div>
                          </div>
                          <div className="col-span-2 md:col-span-1 flex items-center gap-2 justify-end">
                            {s.openEntries.length > 0 && s.openEntries[0] && (
                              <>
                                <button
                                  onClick={() => handleMarkAsPaid(s.openEntries[0].id, 'cash')}
                                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                                  style={{ color: '#cbd5e1'  }}
                                >
                                  Bezahlt
                                </button>
                                <button
                                  onClick={() => addToast('info', `Erinnerung an ${m.vorname} ${m.name} (${m.email})`)}
                                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold transition ${stageColor.bg} ${stageColor.text}`}
                                  style={{ border: `1px solid ${s.stage === 'mahnung2' || s.stage === 'mahnung1' ? 'rgba(244,63,94,0.40)' : 'rgba(245,158,11,0.40)'}` }}
                                >
                                  {s.stage === 'mahnung2' ? 'Stufe 2' : s.stage === 'mahnung1' ? 'Mahnen' : 'Erinnern'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {memberSummaries.length === 0 && (
                      <div className="px-6 py-10 text-center">
                        <p className="text-sm text-gray-600 dark:text-slate-400">Keine Mitglieder gefunden.</p>
                      </div>
                    )}
                  </div>
                </article>
              </section>
            )}

            {/* PER EVENT TAB */}
            {activeTab === 'events' && (
              <section className="pb-6">
                <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold">
                      Pro Event · {events.length} {events.length === 1 ? 'Event' : 'Events'}
                    </div>
                    <h3 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                      {eventSummaries.filter(s => s.open > 0).length} mit offenen Beiträgen
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowNewEventModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition"
                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.40)', color: '#86efac' }}
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Event anlegen
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {eventSummaries
                    .filter(s => !searchTerm || s.event.beschriftung.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map(s => {
                      const ev = s.event;
                      const pct = s.total > 0 ? Math.round((s.paid / s.total) * 100) : 0;
                      return (
                        <article
                          key={ev.id}
                          className={`bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700 ${`rounded-2xl p-5 transition-all duration-300 ${deletingEventId === ev.id ? 'opacity-0 scale-95' : 'opacity-100'}`}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="min-w-0">
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                                {ev.abrechnungsmonat
                                  ? new Date(ev.abrechnungsmonat).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
                                  : 'Ohne Datum'}
                              </div>
                              <h4 className="text-base font-black text-gray-900 dark:text-white truncate">{ev.beschriftung}</h4>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {ev.document_id ? (
                                <button
                                  onClick={() => setDocMenuEventId(ev.id)}
                                  className="p-1.5 rounded-lg text-blue-700 dark:text-blue-300 hover:bg-blue-500/10 transition-colors"
                                  title={ev.document_name || 'Dokument'}
                                >
                                  <PaperClipIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => openDocPicker(ev.id)}
                                  className="p-1.5 rounded-lg text-gray-500 dark:text-slate-500 hover:text-blue-300 hover:bg-white/5 transition-colors"
                                  title="Dokument verknüpfen"
                                >
                                  <PaperClipIcon className="h-4 w-4" />
                                </button>
                              )}
                              {s.entries.length === 0 && (
                                <button
                                  onClick={() => handleDeleteEvent(ev.id)}
                                  className="p-1.5 rounded-lg text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 transition-colors"
                                  title="Event löschen"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div
                              className="rounded-xl px-3 py-2"
                              style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                            >
                              <div className="text-[9px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Offen</div>
                              <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">{formatCurrency(s.open)}</div>
                            </div>
                            <div
                              className="rounded-xl px-3 py-2"
                              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                            >
                              <div className="text-[9px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Bezahlt</div>
                              <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">{formatCurrency(s.paid)}</div>
                            </div>
                          </div>

                          <div className="h-2 rounded-full overflow-hidden mb-2 bg-gray-100 dark:bg-white/5" >
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-slate-500">
                            <span>{pct}% bezahlt</span>
                            <span>{s.memberCount} Mitglieder · {formatCurrency(s.total)}</span>
                          </div>
                        </article>
                      );
                    })}
                  {eventSummaries.length === 0 && (
                    <div className="col-span-full text-center py-10 rounded-2xl bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                      <p className="text-sm text-gray-600 dark:text-slate-400">Keine Events vorhanden.</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <section className="pb-6">
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">
                    Verlauf · letzte Zahlungen
                  </div>
                  <h3 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                    {drinkEntries.filter(e => e.paid === 1).length} {drinkEntries.filter(e => e.paid === 1).length === 1 ? 'Zahlung' : 'Zahlungen'}
                  </h3>
                </div>

                <article
                  className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                >
                  <div className="divide-y" style={{ borderColor: '#1e293b' }}>
                    {drinkEntries
                      .filter(e => e.paid === 1)
                      .filter(e => {
                        if (!searchTerm) return true;
                        const m = members.find(mb => mb.mg_nr === e.member_id);
                        const fullName = m ? `${m.vorname} ${m.name}`.toLowerCase() : '';
                        return fullName.includes(searchTerm.toLowerCase()) || e.event_label.toLowerCase().includes(searchTerm.toLowerCase());
                      })
                      .sort((a, b) => {
                        const da = a.paid_date ? new Date(a.paid_date).getTime() : 0;
                        const db = b.paid_date ? new Date(b.paid_date).getTime() : 0;
                        return db - da;
                      })
                      .slice(0, 100)
                      .map(entry => {
                        const m = members.find(mb => mb.mg_nr === entry.member_id);
                        return (
                          <div key={entry.id} className="px-5 sm:px-6 py-3 flex items-center gap-4" style={{ borderBottom: '1px solid #1e293b' }}>
                            {m && renderMemberAvatar(m, 'sm')}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                                  <CheckCircleIcon className="w-3 h-3" />
                                  Bezahlt
                                </span>
                                {entry.payment_method && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border border-white/10 bg-slate-950/55 text-gray-700 dark:text-slate-300">
                                    {entry.payment_method === 'cash' ? 'Bar' : 'PayPal'}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm font-bold text-gray-900 dark:text-white truncate">
                                {m ? `${m.vorname} ${m.name}` : 'Unbekannt'} · {entry.event_label}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                                {entry.paid_date
                                  ? new Date(entry.paid_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })
                                  : '—'}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-base font-black text-primary-700 dark:text-primary-300 tabular-nums">{formatCurrency(entry.amount)}</div>
                            </div>
                          </div>
                        );
                      })}
                    {drinkEntries.filter(e => e.paid === 1).length === 0 && (
                      <div className="px-6 py-10 text-center">
                        <p className="text-sm text-gray-600 dark:text-slate-400">Noch keine Zahlungen erfasst.</p>
                      </div>
                    )}
                  </div>
                </article>
              </section>
            )}
          </>
        )}

        {/* ===== MODALS ===== */}

        {/* New Event Modal */}
        {showNewEventModal && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => {
              setShowNewEventModal(false);
              setNewEventName('');
              setNewEventMonth(new Date().getMonth() + 1);
              setNewEventYear(new Date().getFullYear());
            }}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[480px] flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                    Neues Event
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Vereinsabend anlegen
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                    Lege Name und Abrechnungsmonat fest.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNewEventModal(false);
                    setNewEventName('');
                    setNewEventMonth(new Date().getMonth() + 1);
                    setNewEventYear(new Date().getFullYear());
                  }}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-gray-600 dark:text-slate-400 font-bold mb-2">
                    Event-Name
                  </label>
                  <input
                    type="text"
                    value={newEventName}
                    onChange={e => setNewEventName(e.target.value)}
                    placeholder="z. B. Sommerfest 2026"
                    autoFocus
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark'  }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-gray-600 dark:text-slate-400 font-bold mb-2">
                    Abrechnungsmonat
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={newEventMonth}
                      onChange={e => setNewEventMonth(Number(e.target.value))}
                      className="flex-1 px-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:border-amber-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                      style={{ colorScheme: 'dark'  }}
                    >
                      {['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'].map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={newEventYear}
                      onChange={e => setNewEventYear(Number(e.target.value))}
                      className="w-28 px-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:border-amber-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                      style={{ colorScheme: 'dark'  }}
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => {
                    setShowNewEventModal(false);
                    setNewEventName('');
                    setNewEventMonth(new Date().getMonth() + 1);
                    setNewEventYear(new Date().getFullYear());
                  }}
                  className="px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCreateEvent}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-slate-950 font-bold text-sm transition"
                  style={{ background: '#10b981' }}
                >
                  <PlusIcon className="w-4 h-4" />
                  Event erstellen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Entry Modal */}
        {showQuickEntryModal && quickEntryEventId && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[520px] flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                    Schnelleingabe
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Beträge erfassen
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                    Mitglied {quickEntryIndex + 1} von {members.length}
                  </p>
                </div>
                <button
                  onClick={() => setShowQuickEntryModal(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1">
                {/* Event-Auswahl */}
                <div className="mb-4">
                  <label className="block text-[11px] uppercase tracking-widest text-gray-600 dark:text-slate-400 font-bold mb-2">
                    Event
                  </label>
                  <select
                    value={quickEntryEventId}
                    onChange={e => {
                      const newEventId = Number(e.target.value);
                      setQuickEntryEventId(newEventId);
                      const currentMember = members[quickEntryIndex];
                      if (currentMember) {
                        const existingEntry = drinkEntries.find(
                          entry => entry.member_id === currentMember.mg_nr && entry.month === newEventId
                        );
                        setQuickEntryValue(existingEntry ? existingEntry.amount.toString() : '');
                      }
                    }}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:border-amber-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark'  }}
                  >
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.beschriftung}{event.abrechnungsmonat ? ` (${new Date(event.abrechnungsmonat).getFullYear()})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Aktuelles Mitglied */}
                {members[quickEntryIndex] && (
                  <div
                    className="rounded-2xl p-4 mb-4"
                    style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {renderMemberAvatar(members[quickEntryIndex], 'lg')}
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-bold text-gray-900 dark:text-white truncate">
                          {members[quickEntryIndex].vorname} {members[quickEntryIndex].name}
                        </div>
                        <div className="text-[11px] text-gray-600 dark:text-slate-400">#{members[quickEntryIndex].mitglied_nr}</div>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={quickEntryValue}
                      onChange={e => setQuickEntryValue(e.target.value)}
                      onKeyDown={handleQuickEntryKeyDown}
                      placeholder="Betrag (z. B. 12,50)"
                      autoFocus
                      className="w-full px-3 py-2.5 rounded-xl text-lg font-bold text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                      style={{ colorScheme: 'dark'  }}
                    />
                    {(() => {
                      const existingEntry = drinkEntries.find(
                        e => e.member_id === members[quickEntryIndex]?.mg_nr && e.month === quickEntryEventId
                      );
                      if (existingEntry && quickEntryValue !== existingEntry.amount.toString()) {
                        return (
                          <div className="mt-2 text-[11px] text-gray-600 dark:text-slate-400">
                            Vorheriger Betrag: <span className="font-bold text-gray-900 dark:text-white">€{existingEntry.amount.toFixed(2)}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                <div className="text-[11px] text-gray-500 dark:text-slate-500 grid grid-cols-2 gap-x-3 gap-y-1 mb-4">
                  <span>↵ Enter — speichern + weiter</span>
                  <span>⇥ Tab — überspringen</span>
                  <span>⇧ Shift+Tab — zurück</span>
                  <span>Esc — abbrechen</span>
                </div>

                <div className="h-2 rounded-full overflow-hidden mb-2 bg-gray-100 dark:bg-white/5" >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${((quickEntryIndex + 1) / members.length) * 100}%`,
                      background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
                    }}
                  />
                </div>

                {(() => {
                  const membersWithEntries = members.filter(member => {
                    const hasEntry = drinkEntries.some(e => e.member_id === member.mg_nr && e.month === quickEntryEventId);
                    const hasQuickEntry = quickEntryValues[member.mg_nr] !== undefined;
                    return hasEntry || hasQuickEntry;
                  });
                  if (membersWithEntries.length > 0) {
                    return (
                      <div className="mt-4 max-h-32 overflow-y-auto rounded-xl p-2" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1">
                          Bereits eingegeben
                        </p>
                        <div className="space-y-1">
                          {membersWithEntries.map(member => {
                            const existingEntry = drinkEntries.find(
                              e => e.member_id === member.mg_nr && e.month === quickEntryEventId
                            );
                            const quickValue = quickEntryValues[member.mg_nr];
                            const displayValue = quickValue || existingEntry?.amount.toFixed(2);
                            return (
                              <div key={member.mg_nr} className="flex justify-between text-xs">
                                <span className="text-gray-600 dark:text-slate-400 truncate">
                                  {member.vorname} {member.name}
                                </span>
                                <span className="font-bold text-gray-900 dark:text-white tabular-nums">€{displayValue}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => setShowQuickEntryModal(false)}
                  className="px-4 py-3 rounded-xl text-gray-800 dark:text-slate-200 text-sm font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Abbrechen
                </button>
                <button
                  onClick={async () => {
                    await fetchData();
                    setShowQuickEntryModal(false);
                  }}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition text-slate-950"
                  style={{ background: '#f59e0b' }}
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Fertig
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.variant === 'single' ? {
            text: 'OK',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: {
              text: 'Abbrechen',
              onPress: () => setConfirmModal({ ...confirmModal, visible: false })
            },
            confirm: {
              text: 'Bestätigen',
              onPress: () => {
                confirmModal.onConfirm?.();
                setConfirmModal({ ...confirmModal, visible: false });
              }
            }
          } : undefined}
        />

        {/* Export Modal */}
        <DrinkListExport
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
        />

        {/* Dokument-Vorschau Modal */}
        {docMenuEventId !== null && (() => {
          const ev = events.find(e => e.id === docMenuEventId);
          if (!ev?.document_id) return null;
          const fileName = ev.document_name || '';
          const fileRef = ev.document_path || fileName;
          const isPdf = /\.pdf$/i.test(fileRef);
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileRef);

          const closePreview = () => setDocMenuEventId(null);

          return (
            <div
              className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
              style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
              onClick={closePreview}
            >
              <div
                className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: '85vh' }}
                onClick={e => e.stopPropagation()}
              >
                <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.30)' }}
                    >
                      <PaperClipIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-300 font-bold">
                        Dokument
                      </div>
                      <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight truncate">{fileName}</h2>
                      <p className="text-gray-600 dark:text-slate-400 text-xs truncate">{ev.beschriftung}</p>
                    </div>
                  </div>
                  <button
                    onClick={closePreview}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                  </button>
                </div>

                <div className="flex-1 overflow-auto flex items-center justify-center" style={{ background: 'rgba(2,6,23,0.5)', minHeight: '300px', maxHeight: '55vh' }}>
                  {docPreviewLoading ? (
                    <div className="flex flex-col items-center gap-2 py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
                      <p className="text-sm text-gray-600 dark:text-slate-400">Wird geladen…</p>
                    </div>
                  ) : docPreviewUrl === 'error' ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-slate-400">Vorschau nicht verfügbar</p>
                    </div>
                  ) : docPreviewUrl && isImage ? (
                    <img src={docPreviewUrl} alt={fileName} className="max-w-full max-h-full object-contain" />
                  ) : docPreviewUrl && isPdf ? (
                    <iframe src={docPreviewUrl} className="w-full h-full border-0" style={{ minHeight: '300px', height: '55vh' }} title="PDF Vorschau" />
                  ) : (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 dark:text-slate-400">Kein unterstütztes Format</p>
                    </div>
                  )}
                </div>

                <div
                  className="px-6 py-5 flex flex-wrap gap-2"
                  style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
                >
                  <button
                    onClick={() => { downloadEventDocument(ev); closePreview(); }}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition text-slate-950"
                    style={{ background: '#0070f3', color: '#fff' }}
                  >
                    <DocumentArrowDownIcon className="w-4 h-4" />
                    Herunterladen
                  </button>
                  <button
                    onClick={() => { closePreview(); openDocPicker(ev.id); }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-gray-800 dark:text-slate-200 font-semibold text-sm transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <PaperClipIcon className="w-4 h-4" />
                    Ändern
                  </button>
                  <button
                    onClick={() => { unlinkDocumentFromEvent(ev.id); closePreview(); }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-rose-700 dark:text-rose-300 font-semibold text-sm transition"
                    style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                  >
                    <TrashIcon className="w-4 h-4" />
                    Entfernen
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Dokument-Picker Modal */}
        {docPickerEventId !== null && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setDocPickerEventId(null)}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[520px] flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-300 font-bold">
                    Dokument
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Verknüpfen
                  </h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                    Wähle ein Dokument aus dem Archiv.
                  </p>
                </div>
                <button
                  onClick={() => setDocPickerEventId(null)}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              <div className="px-6 pb-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-slate-500" />
                  <input
                    type="text"
                    value={docSearchTerm}
                    onChange={e => setDocSearchTerm(e.target.value)}
                    placeholder="Dokument suchen…"
                    autoFocus
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-white/10"
                    style={{ colorScheme: 'dark'  }}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 pb-3">
                {docLoading ? (
                  <p className="text-center text-gray-600 dark:text-slate-400 py-8 text-sm">Lade Dokumente…</p>
                ) : (
                  (() => {
                    const filtered = availableDocuments.filter(d =>
                      !docSearchTerm || d.name.toLowerCase().includes(docSearchTerm.toLowerCase())
                    );
                    return filtered.length > 0 ? (
                      <div className="space-y-1">
                        {filtered.slice(0, 50).map(doc => (
                          <button
                            key={doc.id}
                            onClick={() => linkDocumentToEvent(docPickerEventId, doc.id)}
                            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition"
                            style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}
                          >
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                            >
                              <PaperClipIcon className="h-4 w-4 text-gray-700 dark:text-slate-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{doc.name}</p>
                              <p className="text-[11px] text-gray-500 dark:text-slate-500">{new Date(doc.created_at).toLocaleDateString('de-DE')}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-600 dark:text-slate-400 py-8 text-sm">Keine Dokumente gefunden</p>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {showHelpModal && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowHelpModal(false)}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl flex flex-col animate-slideUp overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: '90vh' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                    Hilfe
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    Verzehrkosten — So funktioniert's
                  </h2>
                </div>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1 space-y-3">
                {([
                  { icon: <CalendarIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />, title: 'Event erstellen', body: 'Klicke auf "Event anlegen" und vergib einen Namen (z. B. "Sommerfest 2026"). Wichtig: Nur Events ohne Beiträge lassen sich wieder löschen.' },
                  { icon: <CurrencyEuroIcon className="w-5 h-5 text-amber-700 dark:text-amber-300" />, title: 'Beträge erfassen', body: 'Doppelklick auf eine Tabellenzelle — oder die "Schnelleingabe" für alle Mitglieder hintereinander.' },
                  { icon: <CheckCircleIcon className="w-5 h-5 text-primary-700 dark:text-primary-300" />, title: 'Zahlungen verwalten', body: 'Markiere Beträge als "Bar" oder "PayPal". Bei aktiver PayPal-IPN werden Zahlungen automatisch erfasst.' },
                  { icon: <DocumentArrowDownIcon className="w-5 h-5 text-blue-700 dark:text-blue-300" />, title: 'Listen exportieren', body: 'Lade Getränkelisten als PDF oder Excel herunter — ideal zur Kassenabrechnung.' },
                ] as const).map((step, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl p-4 flex gap-4"
                    style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      {step.icon}
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-black text-gray-900 dark:text-white">{step.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-slate-400 mt-1 leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                ))}

                <div
                  className="rounded-2xl p-3 lg:p-4"
                  style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.20)' }}
                >
                  <h4 className="text-sm font-black text-blue-200 flex items-center gap-2">
                    <CreditCardIcon className="w-4 h-4" />
                    PayPal IPN
                  </h4>
                  <p className="text-xs text-gray-700 dark:text-slate-300 mt-2 leading-relaxed">
                    Bei aktiviertem PayPal IPN werden Zahlungen automatisch erfasst. Weiterleitung an
                    <code className="mx-1 px-1 rounded bg-black/30 text-blue-200">/paypal</code>,
                    Echtzeit-Bestätigung, automatische Zuordnung zu Mitglied und Event.
                  </p>
                </div>
              </div>

              <div
                className="px-6 py-5 flex justify-end"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition text-slate-950"
                  style={{ background: '#f59e0b' }}
                >
                  Verstanden
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slideUp { animation: slideUp 0.2s ease; }
        `}</style>
      </div>
    </div>
  );
};

export default VerzehrkostenBeta;
